// PRODUCTION — form step config (tabs, builder, preview)
import React, { Fragment, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { CollectDataFormItemType } from 'src/constants/collect-data/enums'
import type { FormStepConfig, StepApprovalType } from 'src/interfaces/onboardingTemplate'
import { openLinksInNewTab } from 'src/utils/richTextHtml'
import { APPROVAL_TYPE_OPTIONS } from '../shared/constants'
import { ConfigRow } from '../shared/layout'
import { SoloCheckbox } from '../shared/SoloCheckbox'
import { RETRO_COLLECT_ROW_LABEL } from '../shared/RetroactiveCollectRow'
import type { ConfigFormProps } from '../shared/types'
import { AddFormItemModal } from './AddFormItemModal'
import { FormItemBlock } from './FormItemBlock'
import { FORM_ITEM_META } from './formItemMeta'
import { SoloNumberInput, SoloSelect } from './soloControls'
import { toLocalItems, toTemplateItems, type LocalFormItem } from './types'
import { flowEditorTextInputSmCss } from '../../flowEditorInputStyles'
import {
  formFieldInputCss,
  formFieldInputReadOnlyCss,
  formFieldTextareaCss,
  formPreviewLabelCss,
  formRequiredMarkCss,
  formTabBtnCss,
} from './formStepStyles'
import { v4 as uuidv4 } from 'uuid'

export const FormStepConfigForm: React.FC<ConfigFormProps<FormStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
  retroactiveExistingContractors,
}) => {
  const [tab, setTab] = useState<'general' | 'builder' | 'preview'>('general')
  const [optional, setOptional] = useState(initialValues.optional)
  const [approvalType, setApprovalType] = useState<string>(initialValues.approvalType)
  const [name, setName] = useState(initialValues.name)
  const [description, setDescription] = useState(initialValues.description)
  const [maxAttempts, setMaxAttempts] = useState(initialValues.maxAttempts)
  const [formItems, setFormItems] = useState<LocalFormItem[]>(() =>
    toLocalItems(initialValues.formItems),
  )
  const [showAddModal, setShowAddModal] = useState(false)

  const prevRef = useRef<string>('')

  useEffect(() => {
    if (readOnly) {
      setShowAddModal(false)
    }
  }, [readOnly])

  useEffect(() => {
    if (retroactiveExistingContractors === null || retroactiveExistingContractors === undefined) {
      return
    }

    if (!optional && retroactiveExistingContractors.checked) {
      retroactiveExistingContractors.onChange(false)
    }
  }, [optional, retroactiveExistingContractors])

  useEffect(() => {
    const config: FormStepConfig = {
      label: initialValues.label,
      optional,
      approvalType: approvalType as StepApprovalType,
      name,
      description,
      maxAttempts,
      formItems: toTemplateItems(formItems),
    }
    const json = JSON.stringify(config)

    if (readOnly) {
      prevRef.current = json

      return
    }

    if (prevRef.current && prevRef.current !== json) {
      onSave(config)
    }

    prevRef.current = json
  }, [
    initialValues.label,
    optional,
    approvalType,
    name,
    description,
    maxAttempts,
    formItems,
    onSave,
    readOnly,
  ])

  const addItems = (types: CollectDataFormItemType[]) => {
    setFormItems((prev) => [
      ...prev,
      ...types.map((type) => ({
        id: uuidv4(),
        type,
        title: '',
        content: '',
        url: '',
        options: [],
        required: false,
        checkAnswers: false,
      })),
    ])
  }

  const updateItem = (id: string, patch: Partial<LocalFormItem>) => {
    setFormItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)))
  }

  const removeItem = (id: string) => {
    setFormItems((prev) => prev.filter((it) => it.id !== id))
  }

  const moveItem = (index: number, dir: 'up' | 'down') => {
    setFormItems((prev) => {
      const next = [...prev]
      const j = dir === 'up' ? index - 1 : index + 1
      const tmp = next[index]
      next[index] = next[j]
      next[j] = tmp

      return next
    })
  }

  const tabs: { key: typeof tab; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'builder', label: 'Form builder' },
    { key: 'preview', label: 'Form preview' },
  ]

  return (
    <div css={tw`flex flex-col`}>
      <div css={tw`flex border-b border-shades-100 border-solid`}>
        {tabs.map((t) => {
          const active = tab === t.key

          return (
            <button
              key={t.key}
              type="button"
              disabled={readOnly}
              onClick={() => {
                if (!readOnly) {
                  setTab(t.key)
                }
              }}
              css={formTabBtnCss(active, readOnly)}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {/* General tab */}
      {tab === 'general' && (
        <Fragment>
          <ConfigRow label="Optional" disabled={readOnly}>
            <SoloCheckbox checked={optional} onChange={setOptional} disabled={readOnly} />
          </ConfigRow>
          {retroactiveExistingContractors !== null &&
            retroactiveExistingContractors !== undefined && (
              <ConfigRow label={RETRO_COLLECT_ROW_LABEL} disabled={readOnly || !optional}>
                <SoloCheckbox
                  checked={retroactiveExistingContractors.checked}
                  onChange={retroactiveExistingContractors.onChange}
                  disabled={readOnly || !optional}
                />
              </ConfigRow>
            )}
          <ConfigRow label="Approval Type" disabled={readOnly}>
            <SoloSelect
              value={approvalType}
              options={APPROVAL_TYPE_OPTIONS}
              onChange={setApprovalType}
              disabled={readOnly}
            />
          </ConfigRow>
          <ConfigRow label="Name" required disabled={readOnly}>
            <input
              value={name}
              readOnly={readOnly}
              onChange={(e) => {
                if (!readOnly) {
                  setName(e.target.value)
                }
              }}
              css={[formFieldInputCss, readOnly ? formFieldInputReadOnlyCss : undefined]}
            />
          </ConfigRow>
          <ConfigRow label="Description" required top disabled={readOnly}>
            <textarea
              value={description}
              readOnly={readOnly}
              onChange={(e) => {
                if (!readOnly) {
                  setDescription(e.target.value)
                }
              }}
              rows={3}
              css={[formFieldTextareaCss, readOnly ? formFieldInputReadOnlyCss : undefined]}
            />
          </ConfigRow>
          <ConfigRow label="Max Attempts" disabled={readOnly}>
            <SoloNumberInput
              value={maxAttempts}
              onChange={setMaxAttempts}
              min={0}
              disabled={readOnly}
            />
          </ConfigRow>
        </Fragment>
      )}

      {/* Builder tab */}
      {tab === 'builder' && (
        <div css={tw`flex flex-col gap-2.5 px-5 py-4`}>
          <div css={tw`mb-1 flex items-center justify-between`}>
            <span css={tw`text-xs font-semibold uppercase tracking-wide text-shades-400`}>
              Items {formItems.length > 0 && `(${formItems.length})`}
            </span>
            <Button
              variant="outlined"
              size="tiny"
              color="white"
              disabled={readOnly}
              onClick={() => {
                if (!readOnly) {
                  setShowAddModal(true)
                }
              }}
              leftIcon={
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              className={tw`!border-shades-200 !text-shades-700 hover:!border-primary-400 hover:!text-primary-600`}
            >
              Add Item
            </Button>
          </div>
          {formItems.length === 0 ? (
            <div
              css={tw`rounded-[10px] border-[1.5px] border-dashed border-shades-200 px-4 py-10 text-center text-[13px] text-shades-400`}
            >
              No items yet — click <strong css={tw`text-shades-600`}>Add Item</strong> to build the
              form
            </div>
          ) : (
            formItems.map((item, i) => (
              <FormItemBlock
                key={item.id}
                item={item}
                index={i}
                total={formItems.length}
                onUpdate={(patch) => updateItem(item.id, patch)}
                onRemove={() => removeItem(item.id)}
                onMove={(dir) => moveItem(i, dir)}
                readOnly={readOnly}
              />
            ))
          )}
        </div>
      )}

      {/* Preview tab */}
      {tab === 'preview' && (
        <div css={tw`flex flex-col gap-5 px-5 py-6`}>
          {formItems.length === 0 ? (
            <div css={tw`px-4 py-10 text-center text-[13px] text-shades-400`}>
              No items to preview — add items in the{' '}
              <strong css={tw`text-shades-600`}>Form builder</strong> tab
            </div>
          ) : (
            formItems.map((item) => {
              const meta = FORM_ITEM_META[item.type]
              const fieldLabel = item.title || meta.label

              if (item.type === CollectDataFormItemType.TextBlock) {
                if (!item.content) {
                  return (
                    <div
                      key={item.id}
                      css={tw`rounded-lg border border-shades-100 bg-shades-50 px-4 py-3.5 text-sm leading-relaxed text-shades-700 border-solid`}
                    >
                      <span css={tw`italic text-shades-400`}>Text content will appear here...</span>
                    </div>
                  )
                }

                return (
                  <div
                    key={item.id}
                    css={tw`rounded-lg border border-shades-100 bg-shades-50 px-4 py-3.5 text-sm leading-relaxed text-shades-700 border-solid`}
                    dangerouslySetInnerHTML={{ __html: openLinksInNewTab(item.content) }}
                  />
                )
              }

              if (
                item.type === CollectDataFormItemType.Radio ||
                item.type === CollectDataFormItemType.Checkbox
              ) {
                return (
                  <div key={item.id} css={tw`flex flex-col gap-2`}>
                    <label css={formPreviewLabelCss(readOnly)}>
                      {fieldLabel}
                      {item.required && <span css={formRequiredMarkCss}> *</span>}
                    </label>
                    {(item.options.length > 0
                      ? item.options
                      : [
                          { id: '1', title: 'Option 1' },
                          { id: '2', title: 'Option 2' },
                        ]
                    ).map((opt, oi) => (
                      <div key={opt.id ?? oi} css={tw`flex items-center gap-2 pl-1`}>
                        {item.type === CollectDataFormItemType.Checkbox ? (
                          <div
                            css={tw`box-border h-4 w-4 rounded border-2 border-solid border-shades-300 bg-shades-0`}
                          />
                        ) : (
                          <div
                            css={tw`box-border h-4 w-4 rounded-full border-2 border-solid border-shades-300 bg-shades-0`}
                          />
                        )}
                        <span css={tw`text-sm text-shades-700`}>{opt.title}</span>
                      </div>
                    ))}
                  </div>
                )
              }

              if (item.type === CollectDataFormItemType.Select) {
                return (
                  <div key={item.id} css={tw`flex flex-col gap-1.5`}>
                    <label css={formPreviewLabelCss(readOnly)}>
                      {fieldLabel}
                      {item.required && <span css={formRequiredMarkCss}> *</span>}
                    </label>
                    <div
                      css={tw`flex h-10 items-center justify-between rounded-lg border border-shades-200 bg-shades-0 px-3 text-sm text-shades-400 border-solid`}
                    >
                      <span>Select an option</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--mui-shades-400)"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>
                )
              }

              if (item.type === CollectDataFormItemType.File) {
                return (
                  <div key={item.id} css={tw`flex flex-col gap-1.5`}>
                    <label css={formPreviewLabelCss(readOnly)}>
                      {fieldLabel}
                      {item.required && <span css={formRequiredMarkCss}> *</span>}
                    </label>
                    <div
                      css={tw`rounded-lg border-[1.5px] border-dashed border-shades-200 px-4 py-6 text-center text-[13px] text-shades-400`}
                    >
                      Drag & drop or{' '}
                      <span css={tw`cursor-pointer font-semibold text-primary-500`}>browse</span>
                    </div>
                  </div>
                )
              }

              let fieldPlaceholder = 'Enter value'

              if (item.type === CollectDataFormItemType.Number) {
                fieldPlaceholder = '0'
              } else if (item.type === CollectDataFormItemType.Date) {
                fieldPlaceholder = 'MM / DD / YYYY'
              }

              return (
                <div key={item.id} css={tw`flex flex-col gap-1.5`}>
                  <label css={formPreviewLabelCss(readOnly)}>
                    {fieldLabel}
                    {item.required && <span css={formRequiredMarkCss}> *</span>}
                  </label>
                  <input
                    readOnly
                    placeholder={fieldPlaceholder}
                    css={[flowEditorTextInputSmCss, tw`text-shades-400`]}
                  />
                </div>
              )
            })
          )}
        </div>
      )}

      {showAddModal && !readOnly && (
        <AddFormItemModal onClose={() => setShowAddModal(false)} onAdd={addItems} />
      )}
    </div>
  )
}
